import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { WizardNavigation } from './WizardNavigation';
import {
  useMassMigrationWizardOrgType,
  useMassMigrationWizardActions,
} from '@/stores/massMigrationWizardStore';
import type { MassMigrationOrgType } from '@/types/massMigration';
import { useAuth } from '@/hooks';
import { hasPermission } from '@/types';

interface OrgTypeOption {
  type: MassMigrationOrgType;
  title: string;
  description: string;
  requiredColumns: string[];
  optionalColumns: string[];
}

// The euid column is required for operators but optional for admins,
// so the New-org column contract depends on the current user's role.
function buildOrgTypeOptions(isAdmin: boolean): OrgTypeOption[] {
  return [
    {
      type: 'New',
      title: 'New Organizations',
      description:
        'Each row creates a brand-new Amplify organization. Provide full organization and owner details.',
      requiredColumns: [
        'bridge_id',
        'org_name',
        'street',
        'city',
        'state',
        'zip',
        'owner_first',
        'owner_last',
        'owner_email',
        'billing',
        'send_invite',
        ...(isAdmin ? [] : ['euid']),
      ],
      optionalColumns: [
        'owner_phone',
        ...(isAdmin ? ['euid'] : []),
      ],
    },
    {
      type: 'Existing',
      title: 'Existing Organizations',
      description:
        'Each row migrates data into an Amplify organization that already exists. Supply the Amplify org UUID for matching.',
      requiredColumns: [
        'bridge_id',
        'amplify_org_uuid',
      ],
      optionalColumns: [],
    },
  ];
}

export function OrgTypeStep() {
  const orgType = useMassMigrationWizardOrgType();
  const { setOrgType, setStep } = useMassMigrationWizardActions();
  const { user } = useAuth();
  const isAdmin = user?.roles?.some((role) => hasPermission(role, 'admin')) ?? false;
  const orgTypeOptions = buildOrgTypeOptions(isAdmin);

  const handleSelect = (type: MassMigrationOrgType) => {
    setOrgType(type);
    setStep(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Select Organization Type</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose whether you are migrating new or existing Amplify organizations. This determines
          the required CSV columns.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {orgTypeOptions.map((option) => {
          const isSelected = orgType === option.type;

          return (
            <button
              key={option.type}
              type="button"
              onClick={() => handleSelect(option.type)}
              className={cn(
                'w-full rounded-lg border-2 p-0 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:border-primary/50 hover:bg-accent/50',
              )}
              aria-pressed={isSelected}
            >
              <Card className="border-0 shadow-none bg-transparent">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{option.title}</CardTitle>
                    {isSelected && (
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <svg
                          className="h-3 w-3"
                          viewBox="0 0 12 12"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M2 6l3 3 5-5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    )}
                  </div>
                  <CardDescription className="text-xs">{option.description}</CardDescription>
                </CardHeader>

                <CardContent className="space-y-3">
                  <div>
                    <p className="mb-1.5 text-xs font-medium text-foreground">Required columns</p>
                    <code className="block rounded bg-muted px-3 py-2 text-xs leading-relaxed text-muted-foreground font-mono whitespace-pre-wrap">
                      {option.requiredColumns.join(', ')}
                    </code>
                  </div>

                  {option.optionalColumns.length > 0 && (
                    <div>
                      <p className="mb-1.5 text-xs font-medium text-foreground">Optional columns</p>
                      <code className="block rounded bg-muted px-3 py-2 text-xs leading-relaxed text-muted-foreground font-mono whitespace-pre-wrap">
                        {option.optionalColumns.join(', ')}
                      </code>
                    </div>
                  )}
                </CardContent>
              </Card>
            </button>
          );
        })}
      </div>

      <WizardNavigation
        nextLabel="Continue"
        nextDisabled={orgType === null}
        onNext={orgType !== null ? () => setStep(1) : undefined}
      />
    </div>
  );
}
